import { localizationService } from '@shared-services/localization-service';
import { debounce } from 'client/utils';
import { computed, makeAutoObservable } from 'mobx';
import { CustomizationType } from '../types/EcomSDK';
import { countUnits } from './checkboxValueUtils';
import { Customization, CustomizationValue } from './types';
import { buildValidationSchema } from './validation';

const { str } = localizationService;

const APPLY_PRICE_DEBOUNCE_MS = 300;

const hasFilledValue = (value: CustomizationValue): boolean => {
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return false;
};

class CustomizationsState {
    list: Customization[] = [];
    values: Record<string, CustomizationValue> = {};
    errors: Record<string, string> = {};
    /**
     * IDs of text/file customizations whose paid-addon price is currently
     * included in the product price. Mutated debounced after value changes so
     * the product price doesn't bounce on every keystroke, and immediately on
     * empty/clear so removal feels responsive.
     */
    appliedPriceIds: Record<string, true> = {};
    validationSchema: Record<
        string,
        Array<(value: CustomizationValue) => boolean | string>
    > = {};
    private debouncedApplyPrice: (id: string) => void;

    constructor(customizations: Customization[] = []) {
        makeAutoObservable(this, {
            validationSchema: false,
            isEmpty: false,
            isValid: false,
            pricesSurcharge: computed,
        });

        this.validationSchema = buildValidationSchema(customizations);
        this.list = customizations;
        this.debouncedApplyPrice = debounce(
            this.applyPriceIfValid,
            APPLY_PRICE_DEBOUNCE_MS,
        );
    }

    updateValue = (customizationId: string, value: CustomizationValue) => {
        this.values[customizationId] = value;
        if (this.errors[customizationId]) {
            delete this.errors[customizationId];
        }

        const customization = this.list.find(
            (c) => c.id === customizationId,
        );

        // CHECKBOX: flag an over-the-limit selection live, instead of waiting
        // for submit. Lower-bound rules (min/exact-too-few) stay submit-only.
        if (customization?.type === CustomizationType.CHECKBOX) {
            const max = customization.limitations?.max_choices;
            const selectedCount = Array.isArray(value) ? value.length : 0;
            if (max && selectedCount > max) {
                this.errors[customizationId] = str(
                    'ui.runtimessr.productCustomizations.errors.checkbox.maxChoices',
                    { max: max.toString() },
                );
            }
            return;
        }

        // Only TEXT/FILE with a non-zero customization.price are debounced.
        if (!customization || !customization.price) {
            return;
        }

        if (!hasFilledValue(value)) {
            // Drop the surcharge immediately when the field is cleared.
            if (this.appliedPriceIds[customizationId]) {
                delete this.appliedPriceIds[customizationId];
            }
            return;
        }

        this.debouncedApplyPrice(customizationId);
    };

    validate = (customizationId: string) => {
        const currentValue = this.values[customizationId];

        for (const rule of this.validationSchema[customizationId]) {
            const error = rule(currentValue);

            if (typeof error === 'string') {
                this.errors[customizationId] = error;
                return;
            }
        }
    };

    validateAll = () => {
        this.list.forEach((customization) => {
            this.validate(customization.id);
        });
    };

    isEmpty = () => {
        return this.list.length === 0;
    };

    isValid = () => {
        return Object.keys(this.errors).length === 0;
    };

    /**
     * Sum of paid-addon prices currently applied to the product.
     * TEXT/FILE: included only when the debounced validate-and-apply pass has
     * confirmed a non-empty + rule-valid value (tracked via appliedPriceIds).
     * CHECKBOX: per-selected-choice prices, computed live.
     */
    get pricesSurcharge(): number {
        return this.list.reduce((sum, customization) => {
            if (customization.type === CustomizationType.CHECKBOX) {
                if (this.errors[customization.id]) {
                    return sum;
                }
                const value = this.values[customization.id];
                if (!hasFilledValue(value)) {
                    return sum;
                }
                const selected = Array.isArray(value)
                    ? (value as string[])
                    : [];
                return (
                    sum +
                    (customization.choices ?? []).reduce(
                        (acc, choice) =>
                            choice.price && choice.label !== undefined
                                ? acc +
                                  choice.price *
                                      countUnits(selected, choice.label)
                                : acc,
                        0,
                    )
                );
            }
            if (this.appliedPriceIds[customization.id]) {
                return sum + (customization.price ?? 0);
            }
            return sum;
        }, 0);
    }

    private isFieldValid = (customizationId: string): boolean => {
        const value = this.values[customizationId];
        const rules = this.validationSchema[customizationId] ?? [];
        for (const rule of rules) {
            if (typeof rule(value) === 'string') {
                return false;
            }
        }
        return true;
    };

    private applyPriceIfValid = (customizationId: string) => {
        const value = this.values[customizationId];
        const valid = this.isFieldValid(customizationId);
        const filled = hasFilledValue(value);

        if (valid && filled) {
            if (!this.appliedPriceIds[customizationId]) {
                this.appliedPriceIds[customizationId] = true;
            }
        } else if (this.appliedPriceIds[customizationId]) {
            delete this.appliedPriceIds[customizationId];
        }
    };
}

export default CustomizationsState;
